<script setup lang="ts">
import Filter from "@/features/account/ui/filter.vue";
import {useAccountStore} from "@/features/account/account.store";
import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import CommonInput from "@/components/common/CommonInput.vue";
import CommonButton from "@/components/common/CommonButton.vue";
import CommonModal from "@/components/common/CommonModal.vue";
import CommonTable from "@/components/common/CommonTable.vue";
import router from "@/router";
import {useAppStore} from "@/store/app.store";
import {useAlert} from "@/utils/alert";
import {IAccount, IAccountStart} from "@/features/account/account.types";
import {RefreshCcw04, Eye, EyeOff, Pencil01} from "utruckin-icons";
import {DateTime} from "luxon";

const accountStore = useAccountStore()
const appStore = useAppStore();
const {show} = useAlert()

const list = ref<IAccount[]>([]);

const loading = ref<boolean>(true);
const editingModal = ref<boolean>(false);
const modalTitle = ref<string>("");
const modalItem = ref<IAccountStart>({
  session_name: "",
  phone_number: "",
  two_step_password: "",
});
const showError = ref<boolean>(false);
const isReconnecting = ref<boolean>(false);
const reconnectingAccountId = ref<string>("");

const codeIsAvailable = ref<boolean>(false);
const passwordType = ref<'text' | 'password'>("password");
const smsCode = ref<string>("");
const timeLeft = ref<number>(180);
let timer: ReturnType<typeof setInterval> | null = null

const formattedTime = computed(() => {
  const minutes = Math.floor(timeLeft.value / 60)
  const seconds = timeLeft.value % 60

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
})

const startTimer = () => {
  timer = setInterval(() => {
    if (timeLeft.value > 0) {
      timeLeft.value--
    } else {
      stopTimer()
    }
  }, 1000)
}

const stopTimer = () => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

const handleRowClicked = (row: any, rowIndex: number) => {
  router.push(`/message/${row.id}`);
}

const getList = async () => {
  list.value = await accountStore.getAccounts(loading);
}

const edit = async (item?: IAccountStart) => {
  editingModal.value = true;
  if (item) {
    modalTitle.value = 'Edit Account';
    modalItem.value = item
  } else {
    modalTitle.value = 'Add Account';
  }
}

const closeModal = () => {
  modalTitle.value = "";
  passwordType.value = 'password'
  codeIsAvailable.value = false
  isReconnecting.value = false
  reconnectingAccountId.value = "";
  stopTimer()
  modalItem.value = {
    session_name: "",
    phone_number: "",
    two_step_password: "",
  }

  showError.value = false;
}

const cancelEditButton = () => {
  editingModal.value = false;
  closeModal()
}

const saveEditButton = async () => {
  if (loading.value) {
    return;
  }
  if (!modalItem.value.session_name || !modalItem.value.phone_number) {
    showError.value = true;
    return
  }

  let request: IAccountStart = {...modalItem.value}

  request.phone_number = request.phone_number.split('+')[1];

  if (isReconnecting.value) {
    await accountStore.reconnectAccount(loading, reconnectingAccountId.value, request, (data: any) => {
      if (data.status === 200) {
        show("Saved successfully", "success");
        codeIsAvailable.value = true;
        startTimer()
      }
    })
  } else {
    await accountStore.startAccount(loading, request, (data: any) => {
      if (data.status === 200) {
        show("Saved successfully", "success");
        codeIsAvailable.value = true;
        startTimer()
      }
    })
  }
}

const confirmAccount = async () => {
  if (loading.value) {
    return;
  }

  let result = {
    session_name: modalItem.value.session_name,
    code: smsCode.value,
  }

  if (isReconnecting.value) {
    await accountStore.confirmReconnect(loading, reconnectingAccountId.value, result, (data: any) => {
      if (data.status === 200) {
        show("Saved successfully", "success");
        cancelEditButton()
        appStore.triggerRefresh()
      }
    })
  } else {
    await accountStore.confirmAccount(loading, result, (data: any) => {
      if (data.status === 200) {
        show("Saved successfully", "success");
        cancelEditButton()
        appStore.triggerRefresh()
      }
    })
  }
}

const handleAppendClickPassword = () => {
  passwordType.value = passwordType.value === "password" ? "text" : "password";
}

const goBackButton = () => {
  stopTimer()
  codeIsAvailable.value = false
}

const formatText = (label: string) => {
  return label.split("_").join(" ")
}

const reconnect = (item: any, e: any) => {
  e.stopPropagation()
  edit(item);
  modalItem.value.phone_number = "+" + item.phone;
  modalItem.value.session_name = item.session_name;
  isReconnecting.value = true;
  reconnectingAccountId.value = item.id;
}

const editItem = (item: any, e: any) => {
  e.stopPropagation()
  edit(item);
  modalItem.value.phone_number = "+" + item.phone;
  modalItem.value.session_name = item.session_name;
}

const utcIsoToZoneRelative = (value: string) => DateTime.fromISO(value, {zone: "utc"}).setZone().toRelative()!

onMounted(() => {
  getList()
})

onUnmounted(() => {
  stopTimer()
})

watch(
    () => appStore.refreshKey,
    () => {
      getList()
    },
    {
      immediate: false,
    }
)
</script>
<template>
  <div class="v-account">
    <Filter @clicked-add="edit" :store="accountStore"/>

    <div class="body">
      <CommonTable
          :items="list"
          :loading="loading"
          :headers="accountStore.headers"
          @row-clicked="handleRowClicked"
      >
        <template #id="{index}">
          {{ index + 1 }}
        </template>

        <template #account="{item}">
          <div class="account">
            <span>{{ item.session_name }}</span>
            <span class="status"
                  :class="item.health.runtime_status == 'disconnected' ? 'disconnected' : 'connected'">
              <span class="label">{{ formatText(item.health.auth_status) }}</span>
            </span>

            <span v-if="item.health.runtime_status == 'disconnected'" class="activity">
              {{ utcIsoToZoneRelative(item.health.last_heartbeat_at) }}
            </span>
          </div>
        </template>

        <template #all="{item}">
          {{ item.messages.total }}
        </template>

        <template #text="{item}">
          {{ item.messages.text }}
        </template>

        <template #voice="{item}">
          {{ item.messages.voice + item.messages.document + item.messages.audio }}
        </template>

        <template #call="{item}">
          {{ item.messages.calls }}
        </template>

        <template #photo="{item}">
          {{ item.messages.photo + item.messages.video + item.messages.round }}
        </template>

        <template #all_errors="{item}">
          {{ item.errors.total }}
        </template>

        <template #reply_time="{item}">
          {{ item.errors.reply_time_exceed }}
        </template>

        <template #unanswered="{item}">
          {{ item.errors.missed_calls + item.errors.declined_calls }}
        </template>

        <template #deleted="{item}">
          {{ item.errors.deleted }}
        </template>

        <template #edited="{item}">
          {{ item.errors.edited }}
        </template>
        <template #actions="{item}">
          <div class="actions">
            <div v-if="item.health.runtime_status == 'disconnected'" class="reconnect"
                 @click="(e: any) => {reconnect(item, e)}">
              <RefreshCcw04 :size="16"/>
            </div>
          </div>
        </template>
      </CommonTable>
    </div>
  </div>

  <CommonModal
      v-model="editingModal"
      :title="modalTitle"
      @close="closeModal"
  >
    <div>
      <CommonInput
          label="Name"
          placeholder="Name"
          v-model:model-value="modalItem.session_name"
          :disabled="codeIsAvailable || loading || isReconnecting"
          :error="!modalItem.session_name && showError"
          errorMessage="Value is required"
      />
      <CommonInput
          label="Phone number"
          placeholder="Phone number"
          v-model:model-value="modalItem.phone_number"
          :disabled="codeIsAvailable || loading || isReconnecting"
          :error="!modalItem.phone_number && showError"
          mask="+###############"
          errorMessage="Value is required"
      />
      <CommonInput
          label="Password"
          placeholder="Password"
          :type="passwordType"
          :append-icon="passwordType == 'password' ? Eye : EyeOff"
          @append-icon-click="handleAppendClickPassword"
          v-model:model-value="modalItem.two_step_password"
          :disabled="codeIsAvailable || loading"
      />
    </div>

    <div v-if="codeIsAvailable" class="code">
      <CommonInput
          label="Sms code"
          placeholder="Sms code"
          v-model:model-value="smsCode"
          :disabled="loading"
          mask="######"
      />

      <div class="code__main">
        <div class="code__main--time">
          <span class="label">Send code again after</span>
          <span class="time">
            {{ formattedTime }}
          </span>
        </div>

        <div class="code__main--action">
          Resend code
        </div>
      </div>
    </div>

    <template #footer>
      <div class="modal__footer">
        <CommonButton
            type="secondary"
            label="Cancel"
            @clicked="cancelEditButton"
        />
        <CommonButton
            v-if="codeIsAvailable"
            type="secondary"
            label="Back"
            @clicked="goBackButton"
        />
        <CommonButton
            v-if="!codeIsAvailable"
            type="primary"
            label="Next"
            :loading="loading"
            @clicked="saveEditButton"
        />
        <CommonButton
            v-else
            type="primary"
            label="Save"
            :loading="loading"
            @clicked="confirmAccount"
        />
      </div>
    </template>
  </CommonModal>
</template>

<style lang='scss' scoped>
.v-account {
  .body {
    padding: 0 1rem;

    .account {
      display: flex;
      align-items: center;
      gap: 8px;

      .status {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        position: relative;

        &.connected {
          background: var(--color-green);
        }

        &.disconnected {
          background: var(--color-red);
        }

        .label {
          opacity: 0;
          text-transform: capitalize;
          position: absolute;
          background: var(--color-background);
          border: 1px solid var(--color-border);
          border-radius: 4px;
          padding: 1px 4px;
          white-space: nowrap;
          left: 120%;
          top: calc(50% - 10px);
          box-shadow: var(--box-shadow);
          transition: 0.3s linear all;
        }

        &:hover .label {
          opacity: 1;
        }
      }

      .activity {
        font-size: 12px;
        line-height: 12px;
        font-weight: 400;
        color: var(--color-secondary-label);
      }
    }

    .actions {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;

      .reconnect {
        width: 31px;
        height: 31px;
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border);
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;

        &:hover {
          background: var(--color-hover);
        }
      }
    }
  }
}

.code {
  border-top: 1px solid var(--color-border);
  padding-top: 16px;

  &__main {
    margin-top: 8px;
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;

    &--time {
      .label {
        margin-left: 8px;
        color: var(--color-label);
      }
    }

    &--action {
      color: var(--color-blue);
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}

.modal__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}
</style>
